import { TimelineLabelWithRenderingParameters } from '../types';

/**
 * Calculates the number and depths of "transit links" crossing each row in a flat,
 * hierarchical list.
 *
 * A "transit link" is a line connecting a parent to its child that passes through
 * the current row. We can determine these by keeping track of all the active
 * parent branches as we iterate through the list.
 *
 * @param treeData A flat array where each object has a 'treeDepth' property.
 * @returns A Map where the key is the item's ID and the value
 * is an array of the depths of the crossing links.
 */
export const getTransitLinksById = (
  treeData: TimelineLabelWithRenderingParameters[]
): Map<string, number[]> => {
  // This array will act as a stack, storing the 'treeDepth' of all currently
  // active parent branches.
  const prevItemActiveDepths: number[] = [];
  const transitLinksById = new Map<string, number[]>();

  // walk backwards to avoid links leading nowhere
  for (let i = treeData.length - 1; i > 0; i--) {
    const currentItem = treeData[i];
    const currentDepth = currentItem.itemModel.treeDepth;
    const prevItem = treeData[i - 1];
    const prevDepth = prevItem.itemModel.treeDepth;

    // Update the activeDepths stack based on the change in depth.
    if (currentDepth < prevDepth && currentDepth > 0) {
      // if the prev item tree depth is larger than the current item tree depth,
      // that means that our current item's link goes past the above item to connect
      // with the parent. => add the depth to the transit links of the prev Item
      prevItemActiveDepths.push(currentDepth);
    } else {
      // A branch has closed. We need to pop from the stack until we
      // are at the correct level for the current item's parent.
      while (
        prevItemActiveDepths.length > 0 &&
        prevItemActiveDepths[prevItemActiveDepths.length - 1] >= currentDepth
      ) {
        prevItemActiveDepths.pop();
      }
    }

    const transitLinkDepths = Array.from(new Set(prevItemActiveDepths));

    // Store the result for this row in the Map.
    transitLinksById.set(prevItem.itemModel.id, transitLinkDepths);
  }

  return transitLinksById;
};
